import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { RouteErrorBoundary } from "@/components/medam/route-error-boundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ESTATUS_POSTULACION,
  ETIQUETA_ESTATUS_POSTULACION,
  formatearFechaHora,
  type EstatusPostulacion,
} from "@/lib/catalogos";
import { listarPostulacionesAdmin } from "@/lib/postulaciones.functions";

export const Route = createFileRoute("/_authenticated/admin/postulaciones/")({
  errorComponent: ({ error, reset }) => (
    <RouteErrorBoundary
      error={error}
      reset={reset}
      titulo="No pudimos cargar las postulaciones"
      descripcion="Ocurrió un problema al abrir el listado de postulaciones."
    />
  ),
  component: PostulacionesAdmin,
});

const TODOS = "todos";

function PostulacionesAdmin() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "postulaciones"],
    queryFn: () => listarPostulacionesAdmin(),
  });
  const [busqueda, setBusqueda] = useState("");
  const [estatus, setEstatus] = useState<string>(TODOS);
  const [posicion, setPosicion] = useState<string>(TODOS);

  if (isLoading) return <Loader2 className="size-6 animate-spin text-primary" />;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;

  const filas = data ?? [];
  const posiciones = Array.from(
    new Map(filas.filter((f) => f.posiciones).map((f) => [f.posiciones!.id, f.posiciones!.nombre])),
  );

  const filtradas = filas.filter((fila) => {
    const texto = `${fila.folio} ${fila.postulantes?.nombre_completo ?? ""} ${fila.postulantes?.correo ?? ""}`.toLowerCase();
    return (
      texto.includes(busqueda.trim().toLowerCase()) &&
      (estatus === TODOS || fila.estatus === estatus) &&
      (posicion === TODOS || fila.posiciones?.id === posicion)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Postulaciones</h1>
        <p className="text-sm text-muted-foreground">
          {filas.length} postulación(es) recibidas en total.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          placeholder="Buscar por folio, nombre o correo"
          value={busqueda}
          onChange={(evento) => setBusqueda(evento.target.value)}
        />
        <Select value={estatus} onValueChange={setEstatus}>
          <SelectTrigger>
            <SelectValue placeholder="Estatus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos los estatus</SelectItem>
            {ESTATUS_POSTULACION.map((valor) => (
              <SelectItem key={valor} value={valor}>
                {ETIQUETA_ESTATUS_POSTULACION[valor]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={posicion} onValueChange={setPosicion}>
          <SelectTrigger>
            <SelectValue placeholder="Posición" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todas las posiciones</SelectItem>
            {posiciones.map(([valor, nombre]) => (
              <SelectItem key={valor} value={valor}>
                {nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtradas.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          No hay postulaciones que coincidan con los filtros.
        </p>
      ) : (
        <ul className="space-y-3">
          {filtradas.map((fila) => (
            <li
              key={fila.id}
              className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card p-5 shadow-sm md:flex-row md:items-center"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{fila.postulantes?.nombre_completo}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {fila.folio} · {fila.posiciones?.nombre} · {fila.postulantes?.correo} ·{" "}
                  {formatearFechaHora(fila.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {fila.calificacion ? (
                  <Badge variant="outline">{fila.calificacion}/5</Badge>
                ) : null}
                <Badge variant="secondary">
                  {ETIQUETA_ESTATUS_POSTULACION[fila.estatus as EstatusPostulacion]}
                </Badge>
                <Button asChild size="sm" variant="outline">
                  <Link to="/admin/postulaciones/$id" params={{ id: fila.id }}>
                    Revisar
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
